import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import { type Country, type PageResolver, ProfileTab } from '~/stores/app/types';
import { handleApiError } from '~/utils/apiClient';

const STORE_ID = 'app';

interface Actions {
  fetchCountries: () => Promise<Country[] | null>;
  fetchPageType: (slug: string, locale: Locale) => Promise<PageResolver | null>;
}

interface Getters extends _GettersTree<State> {
  getCountries: (state: State) => (locale: Locale) => Country[] | null;
  getCurrentCurrency: (state: State) => Currency;
  getPageType: (state: State) => (slug: string | undefined, locale: Locale) => PageResolver | null;
}

interface State {
  countries: Country[] | null;
  currentCountryCode: string;
  pageTypeCache: Map<string, PageResolver | null>;
  profileTab: ProfileTab;
}

export const useAppStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async fetchCountries() {
      try {
        const response = await this.$api.fetch<Country[]>(ApiMethod.GET, '/settings/countries');

        if (response.data) {
          this.countries = response.data;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError, 'warning');

        return null;
      }

      return null;
    },
    async fetchPageType(slug: string, locale: Locale) {
      const cacheKey = `${slug}-${locale}`;

      try {
        const cachedPageType = this.pageTypeCache.get(cacheKey);
        if (cachedPageType) return cachedPageType;

        const response = await this.$api.fetch<PageResolver>(ApiMethod.GET, `/page-resolver/slug/${slug}`, { locale });

        if (response.data) {
          this.pageTypeCache.set(cacheKey, response.data);
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }

      return null;
    },
  },
  getters: {
    getCountries:
      (state) =>
      (locale: Locale): Country[] | null =>
        state.countries?.sort((a, b) => {
          const aCountryName = getCountryName(a, locale);
          const bCountryName = getCountryName(b, locale);

          return aCountryName && bCountryName ? aCountryName.localeCompare(bCountryName) : 0;
        }) ?? null,
    getCurrentCurrency: (state) =>
      state.countries?.find((country) => country.iso_code === state.currentCountryCode)?.currency_iso_code ??
      DEFAULT_CURRENCY,
    getPageType:
      (state) =>
      (slug: string | undefined, locale: Locale): PageResolver | null => {
        if (!slug) return null;

        const cacheKey = `${slug}-${locale}`;
        const cachedPageType = state.pageTypeCache.get(cacheKey);

        if (cachedPageType) return cachedPageType;

        return null;
      },
  },
  persist: {
    pick: ['currentCountryCode'],
    storage: piniaPluginPersistedstate.sessionStorage(),
  },
  state: () => ({
    countries: null,
    currentCountryCode: DEFAULT_COUNTRY,
    pageTypeCache: new Map<string, PageResolver | null>(),
    profileTab: ProfileTab.MY_PROFILE,
  }),
});
